/**
 * TypeScript Representation of 'starknet-specs' (OpenRpc protocol types)
 * https://github.com/starkware-libs/starknet-specs/tree/v0.4.0-rc3
 *
 * Starknet Node Read API  0.4.0 - rpc tag v0.4.0-rc3
 * Starknet Node Write API 0.4.0 - rpc tag v0.4.0-rc3
 * Starknet Node Trace API 0.4.0 - rpc tag v0.4.0-rc3
 */

/**
 * A field element. represented by at most 63 hex digits
 * @pattern ^0x(0|[a-fA-F1-9]{1}[a-fA-F0-9]{0,62})$
 */
export type FELT = string;
export type ADDRESS = FELT;
type NUM_AS_HEX = string;
type SIGNATURE = Array<FELT>;
/**
 * an ethereum address represented as 40 hex digits
 * @pattern ^0x[a-fA-F0-9]{40}$
 */
type ETH_ADDRESS = string;
type BLOCK_NUMBER = number;
type BLOCK_HASH = FELT;
type TXN_HASH = FELT;

export enum TXN_TYPE {
  DECLARE = 'DECLARE',
  DEPLOY = 'DEPLOY',
  DEPLOY_ACCOUNT = 'DEPLOY_ACCOUNT',
  INVOKE = 'INVOKE',
  L1_HANDLER = 'L1_HANDLER',
}

type BLOCK_STATUS = 'PENDING' | 'ACCEPTED_ON_L2' | 'ACCEPTED_ON_L1' | 'REJECTED';
enum BLOCK_TAG {
  latest = 'latest',
  pending = 'pending',
}
type BLOCK_ID = { block_hash: BLOCK_HASH } | { block_number: BLOCK_NUMBER } | BLOCK_TAG;
type MSG_TO_L1 = {
  to_address: FELT;
  payload: Array<FELT>;
};

type MSG_FROM_L1 = {
  from_address: ETH_ADDRESS;
  to_address: ADDRESS;
  entry_point_selector: FELT;
  payload: FELT[];
};

export enum TXN_FINALITY_STATUS {
  ACCEPTED_ON_L2 = 'ACCEPTED_ON_L2',
  ACCEPTED_ON_L1 = 'ACCEPTED_ON_L1',
}

export enum TXN_EXECUTION_STATUS {
  SUCCEEDED = 'SUCCEEDED',
  REVERTED = 'REVERTED',
}

type EVENT = {
  from_address: FELT;
  keys: Array<FELT>;
  data: Array<FELT>;
};

type COMMON_RECEIPT_PROPERTIES = {
  transaction_hash: TXN_HASH;
  actual_fee: FELT;
  execution_status: TXN_EXECUTION_STATUS;
  finality_status: TXN_FINALITY_STATUS;
  block_hash?: BLOCK_HASH;
  block_number?: BLOCK_NUMBER;
  messages_sent: Array<MSG_TO_L1>;
  events: Array<EVENT>;
  revert_reason?: string;
};

type PENDING_COMMON_RECEIPT_PROPERTIES = {
  transaction_hash: TXN_HASH;
  actual_fee: FELT;
  type?: TXN_TYPE;
  messages_sent: Array<MSG_TO_L1>;
  events: Array<EVENT>;
  execution_status: TXN_EXECUTION_STATUS;
  finality_status: TXN_FINALITY_STATUS;
  revert_reason?: string;
};

type INVOKE_TXN_RECEIPT = { type: 'INVOKE' } & COMMON_RECEIPT_PROPERTIES;
type DECLARE_TXN_RECEIPT = { type: 'DECLARE' } & COMMON_RECEIPT_PROPERTIES;

type DEPLOY_TXN_RECEIPT = {
  type: 'DEPLOY';
  contract_address: FELT;
} & COMMON_RECEIPT_PROPERTIES;

type L1_HANDLER_TXN_RECEIPT = { type: 'L1_HANDLER' } & COMMON_RECEIPT_PROPERTIES;

type PENDING_DEPLOY_TXN_RECEIPT = {
  contract_address: FELT;
} & PENDING_COMMON_RECEIPT_PROPERTIES;

type PENDING_TXN_RECEIPT = PENDING_DEPLOY_TXN_RECEIPT | PENDING_COMMON_RECEIPT_PROPERTIES;

type TXN_RECEIPT =
  | INVOKE_TXN_RECEIPT
  | L1_HANDLER_TXN_RECEIPT
  | DECLARE_TXN_RECEIPT
  | DEPLOY_TXN_RECEIPT
  | DEPLOY_ACCOUNT_TXN_RECEIPT
  | PENDING_TXN_RECEIPT;

type BLOCK_HEADER = {
  block_hash: BLOCK_HASH;
  parent_hash: BLOCK_HASH;
  block_number: BLOCK_NUMBER;
  new_root: FELT;
  timestamp: number;
  sequencer_address: FELT;
};
type BLOCK_BODY_WITH_TX_HASHES = {
  transactions: Array<TXN_HASH>;
};
type BLOCK_WITH_TX_HASHES = {
  status: BLOCK_STATUS;
} & BLOCK_HEADER &
  BLOCK_BODY_WITH_TX_HASHES;
type PENDING_BLOCK_WITH_TX_HASHES = BLOCK_BODY_WITH_TX_HASHES & {
  timestamp: number;
  sequencer_address: FELT;
  parent_hash: BLOCK_HASH;
};

type FUNCTION_CALL = {
  contract_address: ADDRESS;
  entry_point_selector: FELT;
  calldata: Array<FELT>;
};

type INVOKE_TXN = INVOKE_TXN_V0 | INVOKE_TXN_V1;

type DECLARE_TXN = DECLARE_TXN_V1 | DECLARE_TXN_V2;

type DECLARE_TXN_V1 = {
  type: 'DECLARE';
  sender_address: ADDRESS;
  max_fee: FELT;
  version: '0x1';
  signature: SIGNATURE;
  nonce: FELT;
} & ({ contract_class: DEPRECATED_CONTRACT_CLASS } | { class_hash: FELT });

type DECLARE_TXN_V2 = {
  type: 'DECLARE';
  sender_address: ADDRESS;
  compiled_class_hash: FELT;
  max_fee: FELT;
  version: '0x2';
  signature: SIGNATURE;
  nonce: FELT;
} & ({ contract_class: CONTRACT_CLASS } | { class_hash: FELT });

type DEPLOY_ACCOUNT_TXN = {
  type: 'DEPLOY_ACCOUNT';
  max_fee: FELT;
  version: NUM_AS_HEX;
  signature: SIGNATURE;
  nonce: FELT;
  contract_address_salt: FELT;
  constructor_calldata: FELT[];
  class_hash: FELT;
};

type DEPLOY_TXN = {
  type: 'DEPLOY';
  contract_address_salt: FELT;
  constructor_calldata: FELT[];
  class_hash: FELT;
  version: NUM_AS_HEX;
};

type DEPLOY_ACCOUNT_TXN_RECEIPT = COMMON_RECEIPT_PROPERTIES & {
  type: 'DEPLOY_ACCOUNT';
  contract_address: FELT;
};

type TXN = INVOKE_TXN | L1_HANDLER_TXN | DECLARE_TXN | DEPLOY_TXN | DEPLOY_ACCOUNT_TXN;

type TXN_WITH_HASH = TXN & { transaction_hash: TXN_HASH };

enum L1_HANDLER {
  'L1_HANDLER',
}

type L1_HANDLER_TXN = {
  version: NUM_AS_HEX;
  type: L1_HANDLER;
  nonce: NUM_AS_HEX;
} & FUNCTION_CALL;

type INVOKE_TXN_V0 = {
  type: 'INVOKE';
  max_fee: FELT;
  version: '0x0';
  signature: SIGNATURE;
  contract_address: ADDRESS;
  entry_point_selector: FELT;
  calldata: FELT[];
};

type INVOKE_TXN_V1 = {
  type: 'INVOKE';
  sender_address: ADDRESS;
  calldata: FELT[];
  max_fee: FELT;
  version: '0x1';
  signature: SIGNATURE;
  nonce: FELT;
};

type BLOCK_BODY_WITH_TXS = {
  transactions: Array<TXN_WITH_HASH>;
};
type BLOCK_WITH_TXS = {
  status: BLOCK_STATUS;
} & BLOCK_HEADER &
  BLOCK_BODY_WITH_TXS;
type PENDING_BLOCK_WITH_TXS = BLOCK_BODY_WITH_TXS & {
  timestamp: number;
  sequencer_address: FELT;
  parent_hash: BLOCK_HASH;
};

type CONTRACT_CLASS = {
  sierra_program: Array<FELT>;
  contract_class_version: string;
  entry_points_by_type: {
    CONSTRUCTOR: Array<SIERRA_ENTRY_POINT>;
    EXTERNAL: Array<SIERRA_ENTRY_POINT>;
    L1_HANDLER: Array<SIERRA_ENTRY_POINT>;
  };
  abi?: string;
};

type DEPRECATED_CONTRACT_CLASS = {
  program: string;
  entry_points_by_type: {
    CONSTRUCTOR: Array<DEPRECATED_CAIRO_ENTRY_POINT>;
    EXTERNAL: Array<DEPRECATED_CAIRO_ENTRY_POINT>;
    L1_HANDLER: Array<DEPRECATED_CAIRO_ENTRY_POINT>;
  };
  abi?: CONTRACT_ABI;
};

type CONTRACT_ABI = Array<CONTRACT_ABI_ENTRY>;

type CONTRACT_ABI_ENTRY = FUNCTION_ABI_ENTRY | EVENT_ABI_ENTRY | STRUCT_ABI_ENTRY;

enum STRUCT_ABI_TYPE {
  'struct',
}
enum EVENT_ABI_TYPE {
  'event',
}
enum FUNCTION_ABI_TYPE {
  'function',
  'l1_handler',
  'constructor',
}

type STRUCT_ABI_ENTRY = STRUCT_ABI_TYPE & {
  name: string;
  size: number;
  members: Array<STRUCT_MEMBER>;
};

type STRUCT_MEMBER = {
  offset: number;
} & TYPED_PARAMETER;

type EVENT_ABI_ENTRY = {
  name: string;
  keys: Array<TYPED_PARAMETER>;
  data: Array<TYPED_PARAMETER>;
} & EVENT_ABI_TYPE;

type FUNCTION_ABI_ENTRY = {
  type: FUNCTION_ABI_TYPE;
  name: string;
  inputs: Array<TYPED_PARAMETER>;
  outputs: Array<TYPED_PARAMETER>;
};

type TYPED_PARAMETER = {
  name: string;
  type: string;
};

type DEPRECATED_CAIRO_ENTRY_POINT = {
  offset: NUM_AS_HEX;
  selector: FELT;
};
type SIERRA_ENTRY_POINT = { selector: FELT; function_idx: number };

export type CONTRACT_STORAGE_DIFF_ITEM = {
  address: FELT;
  storage_entries: { key: FELT; value: FELT }[];
};

type DEPLOYED_CONTRACT_ITEM = {
  address: FELT;
  class_hash: FELT;
};
type STATE_UPDATE = {
  block_hash: BLOCK_HASH;
  new_root: FELT;
} & PENDING_STATE_UPDATE;
type PENDING_STATE_UPDATE = {
  old_root: FELT;
  state_diff: {
    storage_diffs: Array<CONTRACT_STORAGE_DIFF_ITEM>;
    deprecated_declared_classes: Array<FELT>;
    declared_classes: Array<{ class_hash: FELT; compiled_class_hash: FELT }>;
    deployed_contracts: Array<DEPLOYED_CONTRACT_ITEM>;
    replaced_classes: Array<{ contract_address: ADDRESS; class_hash: FELT }>;
    nonces: Array<{
      contract_address: ADDRESS;
      nonce: FELT;
    }>;
  };
};

/**
 * A storage key. Represented as up to 62 hex digits, 3 bits, and 5 leading zeroes.
 * @pattern ^0x0[0-7]{1}[a-fA-F0-9]{0,62}$
 */
type STORAGE_KEY = string;

type EVENT_FILTER = {
  from_block?: BLOCK_ID;
  to_block?: BLOCK_ID;
  address?: ADDRESS;
  keys?: Array<Array<FELT>>;
};
type EVENTS_CHUNK = {
  events: Array<EMITTED_EVENT>;
  continuation_token?: string;
};
type RESULT_PAGE_REQUEST = {
  continuation_token?: string;
  chunk_size: number;
};
type EMITTED_EVENT = EVENT & {
  block_hash: BLOCK_HASH;
  block_number: BLOCK_NUMBER;
  transaction_hash: TXN_HASH;
};
type SYNC_STATUS = {
  starting_block_hash: BLOCK_HASH;
  starting_block_num: BLOCK_NUMBER;
  current_block_hash: BLOCK_HASH;
  current_block_num: BLOCK_NUMBER;
  highest_block_hash: BLOCK_HASH;
  highest_block_num: BLOCK_NUMBER;
};
type FEE_ESTIMATE = {
  gas_consumed: NUM_AS_HEX;
  gas_price: NUM_AS_HEX;
  overall_fee: NUM_AS_HEX;
};

enum CALL_TYPE {
  'LIBRARY_CALL',
  'CALL',
}
enum ENTRY_POINT_TYPE {
  'EXTERNAL',
  'L1_HANDLER',
  'CONSTRUCTOR',
}
type FUNCTION_INVOCATION = FUNCTION_CALL & {
  caller_address: FELT;
  class_hash: FELT;
  entry_point_type: ENTRY_POINT_TYPE;
  call_type: CALL_TYPE;
  result: FELT;
  calls: NESTED_CALL;
  events: Array<EVENT>;
  messages: MSG_TO_L1;
};
type NESTED_CALL = FUNCTION_INVOCATION;

type INVOKE_TXN_TRACE = {
  validate_invocation: FUNCTION_INVOCATION;
  execute_invocation: FUNCTION_INVOCATION | { revert_reason: string };
  fee_transfer_invocation: FUNCTION_INVOCATION;
};

type DECLARE_TXN_TRACE = {
  validate_invocation: FUNCTION_INVOCATION;
  fee_transfer_invocation: FUNCTION_INVOCATION;
};

type DEPLOY_ACCOUNT_TXN_TRACE = {
  validate_invocation: FUNCTION_INVOCATION;
  constructor_invocation: FUNCTION_INVOCATION;
  fee_transfer_invocation: FUNCTION_INVOCATION;
};

type L1_HANDLER_TXN_TRACE = {
  function_invocation: FUNCTION_INVOCATION;
};

type TRANSACTION_TRACE =
  | INVOKE_TXN_TRACE
  | DECLARE_TXN_TRACE
  | DEPLOY_ACCOUNT_TXN_TRACE
  | L1_HANDLER_TXN_TRACE;

export enum SIMULATION_FLAG {
  SKIP_VALIDATE = 'SKIP_VALIDATE',
  SKIP_FEE_CHARGE = 'SKIP_FEE_CHARGE',
}

export namespace OPENRPC {
  export type Nonce = FELT;
  export type BlockWithTxHashes = BLOCK_WITH_TX_HASHES | PENDING_BLOCK_WITH_TX_HASHES;
  export type BlockWithTxs = BLOCK_WITH_TXS | PENDING_BLOCK_WITH_TXS;
  export type StateUpdate = STATE_UPDATE | PENDING_STATE_UPDATE;
  export type Storage = FELT;
  export type Transaction = TXN;
  export type TransactionWithHash = TXN_WITH_HASH;
  export type TransactionReceipt = TXN_RECEIPT;
  export type ContractClass = CONTRACT_CLASS;
  export type DeprecatedContractClass = DEPRECATED_CONTRACT_CLASS;
  export type CallResponse = Array<FELT>;
  export type EstimatedFee = FEE_ESTIMATE;
  export type BlockNumber = BLOCK_NUMBER;
  export type BlockHashAndNumber = {
    block_hash: BLOCK_HASH;
    block_number: BLOCK_NUMBER;
  };
  export type CHAIN_ID = string;
  export type PendingTransactions = Array<TransactionWithHash>;
  export type SyncingStatus = false | SYNC_STATUS;
  export type Events = EVENTS_CHUNK;
  export type Trace = TRANSACTION_TRACE;
  export type Traces = Array<{
    transaction_hash: FELT;
    trace_root: TRANSACTION_TRACE;
  }>;
  export type TransactionHash = TXN_HASH;
  export type BlockHash = BLOCK_HASH;
  export type EventFilter = EVENT_FILTER & RESULT_PAGE_REQUEST;
  export type InvokedTransaction = { transaction_hash: TXN_HASH };
  export type DeclaredTransaction = { transaction_hash: TXN_HASH; class_hash: FELT };
  export type DeployedTransaction = { transaction_hash: TXN_HASH; contract_address: FELT };

  export type SimulationFlags = Array<SIMULATION_FLAG>;
  export type SimulatedTransaction = {
    transaction_trace: Trace;
    fee_estimation: EstimatedFee;
  };
  export type SimulatedTransactions = SimulatedTransaction[];

  export type BaseTransaction = DECLARE_TXN | INVOKE_TXN | DEPLOY_ACCOUNT_TXN;

  // Final Methods
  export type Methods = {
    // Read API
    starknet_getBlockWithTxHashes: {
      params: { block_id: BLOCK_ID };
      result: BlockWithTxHashes;
      errors: Errors.BLOCK_NOT_FOUND;
    };
    starknet_getBlockWithTxs: {
      params: { block_id: BLOCK_ID };
      result: BlockWithTxs;
      errors: Errors.BLOCK_NOT_FOUND;
    };
    starknet_getStateUpdate: {
      params: { block_id: BLOCK_ID };
      result: StateUpdate;
      errors: Errors.BLOCK_NOT_FOUND;
    };
    starknet_getStorageAt: {
      params: { contract_address: ADDRESS; key: STORAGE_KEY; block_id: BLOCK_ID };
      result: Storage;
      errors: Errors.CONTRACT_NOT_FOUND | Errors.BLOCK_NOT_FOUND;
    };
    starknet_getTransactionByHash: {
      params: { transaction_hash: TXN_HASH };
      result: TransactionWithHash;
      errors: Errors.TXN_HASH_NOT_FOUND;
    };
    starknet_getTransactionByBlockIdAndIndex: {
      params: { block_id: BLOCK_ID; index: number };
      result: TransactionWithHash;
      errors: Errors.BLOCK_NOT_FOUND | Errors.INVALID_TXN_INDEX;
    };
    starknet_getTransactionReceipt: {
      params: { transaction_hash: TXN_HASH };
      result: TransactionReceipt;
      errors: Errors.TXN_HASH_NOT_FOUND;
    };
    starknet_getClass: {
      params: { block_id: BLOCK_ID; class_hash: FELT };
      result: ContractClass | DeprecatedContractClass;
      errors: Errors.BLOCK_NOT_FOUND | Errors.CLASS_HASH_NOT_FOUND;
    };
    starknet_getClassHashAt: {
      params: { block_id: BLOCK_ID; contract_address: ADDRESS };
      result: FELT;
      errors: Errors.BLOCK_NOT_FOUND | Errors.CONTRACT_NOT_FOUND;
    };
    starknet_getClassAt: {
      params: { block_id: BLOCK_ID; contract_address: ADDRESS };
      result: ContractClass | DeprecatedContractClass;
      errors: Errors.BLOCK_NOT_FOUND | Errors.CONTRACT_NOT_FOUND;
    };
    starknet_getBlockTransactionCount: {
      params: { block_id: BLOCK_ID };
      result: number;
      errors: Errors.BLOCK_NOT_FOUND;
    };
    starknet_call: {
      params: { request: FUNCTION_CALL; block_id: BLOCK_ID };
      result: Array<FELT>;
      errors:
        | Errors.CONTRACT_NOT_FOUND
        | Errors.INVALID_MESSAGE_SELECTOR
        | Errors.INVALID_CALL_DATA
        | Errors.CONTRACT_ERROR
        | Errors.BLOCK_NOT_FOUND;
    };
    starknet_estimateFee: {
      params: { request: Array<BaseTransaction>; block_id: BLOCK_ID };
      result: Array<FEE_ESTIMATE>;
      errors: Errors.CONTRACT_NOT_FOUND | Errors.CONTRACT_ERROR | Errors.BLOCK_NOT_FOUND;
    };
    /**
     * estimate the L2 fee of a message sent on L1
     * estimates the resources required by the l1_handler transaction induced by the message
     */
    starknet_estimateMessageFee: {
      params: { message: MSG_FROM_L1; block_id: BLOCK_ID };
      result: FEE_ESTIMATE;
      errors: Errors.CONTRACT_NOT_FOUND | Errors.CONTRACT_ERROR | Errors.BLOCK_NOT_FOUND;
    };
    starknet_blockNumber: {
      params: {};
      result: BLOCK_NUMBER;
      errors: Errors.NO_BLOCKS;
    };
    starknet_blockHashAndNumber: {
      params: {};
      result: BLOCK_HASH & BLOCK_NUMBER;
      errors: Errors.NO_BLOCKS;
    };
    starknet_chainId: {
      params: {};
      result: CHAIN_ID;
    };
    starknet_pendingTransactions: {
      params: {};
      result: PendingTransactions;
    };
    starknet_syncing: {
      params: {};
      result: SyncingStatus;
    };
    starknet_getEvents: {
      params: { filter: EVENT_FILTER & RESULT_PAGE_REQUEST };
      result: Events;
      errors:
        | Errors.PAGE_SIZE_TOO_BIG
        | Errors.INVALID_CONTINUATION_TOKEN
        | Errors.BLOCK_NOT_FOUND
        | Errors.TOO_MANY_KEYS_IN_FILTER;
    };
    starknet_getNonce: {
      params: { block_id: BLOCK_ID; contract_address: ADDRESS };
      result: FELT;
      errors: Errors.BLOCK_NOT_FOUND | Errors.CONTRACT_NOT_FOUND;
    };

    // Write API
    starknet_addInvokeTransaction: {
      params: {
        invoke_transaction: INVOKE_TXN_V1;
      };
      result: InvokedTransaction;
      errors:
        | Errors.INSUFFICIENT_ACCOUNT_BALANCE
        | Errors.INSUFFICIENT_MAX_FEE
        | Errors.INVALID_TRANSACTION_NONCE
        | Errors.VALIDATION_FAILURE
        | Errors.NON_ACCOUNT
        | Errors.DUPLICATE_TX
        | Errors.UNSUPPORTED_TX_VERSION
        | Errors.UNEXPECTED_ERROR;
    };

    starknet_addDeclareTransaction: {
      params: {
        declare_transaction: DECLARE_TXN;
      };
      result: DeclaredTransaction;
      errors:
        | Errors.CLASS_ALREADY_DECLARED
        | Errors.COMPILATION_FAILED
        | Errors.COMPILED_CLASS_HASH_MISMATCH
        | Errors.INSUFFICIENT_ACCOUNT_BALANCE
        | Errors.INSUFFICIENT_MAX_FEE
        | Errors.INVALID_TRANSACTION_NONCE
        | Errors.VALIDATION_FAILURE
        | Errors.NON_ACCOUNT
        | Errors.DUPLICATE_TX
        | Errors.CONTRACT_CLASS_SIZE_IS_TOO_LARGE
        | Errors.UNSUPPORTED_TX_VERSION
        | Errors.UNSUPPORTED_CONTRACT_CLASS_VERSION
        | Errors.UNEXPECTED_ERROR;
    };

    starknet_addDeployAccountTransaction: {
      params: {
        deploy_account_transaction: DEPLOY_ACCOUNT_TXN;
      };
      result: {
        transaction_hash: TXN_HASH;
        contract_address: FELT;
      };
      errors:
        | Errors.INSUFFICIENT_ACCOUNT_BALANCE
        | Errors.INSUFFICIENT_MAX_FEE
        | Errors.INVALID_TRANSACTION_NONCE
        | Errors.VALIDATION_FAILURE
        | Errors.NON_ACCOUNT
        | Errors.CLASS_HASH_NOT_FOUND
        | Errors.DUPLICATE_TX
        | Errors.UNSUPPORTED_TX_VERSION
        | Errors.UNEXPECTED_ERROR;
    };

    // Trace API
    starknet_traceTransaction: {
      params: { transaction_hash: TXN_HASH };
      result: Trace;
      errors: Errors.INVALID_TXN_HASH | Errors.NO_TRACE_AVAILABLE;
    };
    starknet_traceBlockTransactions: {
      params: { block_hash: BLOCK_HASH };
      result: Traces;
      errors: Errors.INVALID_BLOCK_HASH;
    };
    /**
     * simulate a given sequence of transactions on the requested state, and generate the execution traces.
     * If one of the transactions is reverted, raises CONTRACT_ERROR
     */
    starknet_simulateTransactions: {
      params: {
        block_id: BLOCK_ID;
        transactions: Array<BaseTransaction>;
        simulation_flags: Array<SIMULATION_FLAG>;
      };
      result: SimulatedTransactions;
      errors: Errors.CONTRACT_NOT_FOUND | Errors.CONTRACT_ERROR | Errors.BLOCK_NOT_FOUND;
    };
  };
}

export namespace Errors {
  export interface FAILED_TO_RECEIVE_TXN {
    code: 1;
    message: 'Failed to write transaction';
  }

  export interface NO_TRACE_AVAILABLE {
    code: 10;
    message: 'No trace available for transaction';
    data: {
      status: 'RECEIVED' | 'REJECTED';
    };
  }

  export interface CONTRACT_NOT_FOUND {
    code: 20;
    message: 'Contract not found';
  }

  export interface INVALID_MESSAGE_SELECTOR {
    code: 21;
    message: 'Invalid message selector';
  }

  export interface INVALID_CALL_DATA {
    code: 22;
    message: 'Invalid call data';
  }

  export interface BLOCK_NOT_FOUND {
    code: 24;
    message: 'Block not found';
  }

  export interface INVALID_TXN_HASH {
    code: 25;
    message: 'Invalid transaction hash';
  }

  export interface INVALID_BLOCK_HASH {
    code: 26;
    message: 'Invalid block hash';
  }

  export interface INVALID_TXN_INDEX {
    code: 27;
    message: 'Invalid transaction index in a block';
  }

  export interface CLASS_HASH_NOT_FOUND {
    code: 28;
    message: 'Class hash not found';
  }

  export interface TXN_HASH_NOT_FOUND {
    code: 29;
    message: 'Transaction hash not found';
  }

  export interface PAGE_SIZE_TOO_BIG {
    code: 31;
    message: 'Requested page size is too big';
  }

  export interface NO_BLOCKS {
    code: 32;
    message: 'There are no blocks';
  }

  export interface INVALID_CONTINUATION_TOKEN {
    code: 33;
    message: 'The supplied continuation token is invalid or unknown';
  }

  export interface TOO_MANY_KEYS_IN_FILTER {
    code: 34;
    message: 'Too many keys provided in a filter';
  }

  export interface CONTRACT_ERROR {
    code: 40;
    message: 'Contract error';
  }

  export interface CLASS_ALREADY_DECLARED {
    code: 51;
    message: 'Class already declared';
  }

  export interface INVALID_TRANSACTION_NONCE {
    code: 52;
    message: 'Invalid transaction nonce';
  }

  export interface INSUFFICIENT_MAX_FEE {
    code: 53;
    message: 'Max fee is smaller than the minimal transaction cost (validation plus fee transfer)';
  }

  export interface INSUFFICIENT_ACCOUNT_BALANCE {
    code: 54;
    message: "Account balance is smaller than the transaction's max_fee";
  }

  export interface VALIDATION_FAILURE {
    code: 55;
    message: 'Account validation failed';
  }

  export interface COMPILATION_FAILED {
    code: 56;
    message: 'Compilation failed';
  }

  export interface CONTRACT_CLASS_SIZE_IS_TOO_LARGE {
    code: 57;
    message: 'Contract class size it too large';
  }

  export interface NON_ACCOUNT {
    code: 58;
    message: 'Sender address in not an account contract';
  }

  export interface DUPLICATE_TX {
    code: 59;
    message: 'A transaction with the same hash already exists in the mempool';
  }

  export interface COMPILED_CLASS_HASH_MISMATCH {
    code: 60;
    message: 'the compiled class hash did not match the one supplied in the transaction';
  }

  export interface UNSUPPORTED_TX_VERSION {
    code: 61;
    message: 'the transaction version is not supported';
  }

  export interface UNSUPPORTED_CONTRACT_CLASS_VERSION {
    code: 62;
    message: 'the contract class version is not supported';
  }

  export interface UNEXPECTED_ERROR {
    code: 63;
    message: 'An unexpected error occured';
    data: string;
  }
}
